import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Button, TextField, Typography, useIsMounted, useOverlayContext } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import commonStyles from '~/components/ChangeEmailAddressModal/ChangeEmailAddress.module.scss';
import { ViewType, changeEmailErrorMessages, changeEmailSuccessMessages } from '~/components/ChangeEmailAddressModal/ChangeEmailAddressTypes';
import OTPInputSection from '~/components/ChangeEmailAddressModal/OTPInputSection';
import { useIoContext } from '~/contexts';
import { useSendOTPCode } from '~/hooks/useSendOTPCode/useSendOTPCode';
import { useSetAuthToken } from '~/hooks/useSetAuthToken';
import { useLocaleKeys } from '~/i18n';
import { ServerError } from '~/infra/ErrorResponse';
import { handleRequest } from '~/infra/utils';
import { useStoreActions } from '~/store/store-context';
export const GetVerifyNewEmailTID = (parentTestId)=>getTestIds({
        parentTestId,
        deprecatedTestIds: {
            VerifyNewEmailTID: {
                Root: 'verify-email-address-view',
                InfoText: 'verify-email-address-info-text',
                EmailInput: 'verify-email-address',
                CancelButton: 'verify-email-address-cancel-button',
                SubmitButton: 'verify-otp-submit-button'
            }
        }
    });
const VerifyNewEmail = (param)=>{
    let { handleClose, newUserEmail, handleSetView } = param;
    const { t: changeEmailModalTexts } = useLocaleKeys('components.changeEmailModal');
    const { VerifyNewEmailTID } = useMemo(()=>GetVerifyNewEmailTID(), []);
    const { api } = useStoreActions();
    const setAuthToken = useSetAuthToken();
    const isMounted = useIsMounted();
    const { sendOTPCode } = useSendOTPCode();
    const overlayContext = useOverlayContext();
    const { cookies } = useIoContext();
    const [isLoading, setIsLoading] = useState(false);
    const { control, handleSubmit, setError, reset, watch, clearErrors } = useForm({
        mode: 'onSubmit',
        reValidateMode: 'onSubmit',
        defaultValues: {
            otp: ''
        }
    });
    const handleChangeEmailAndAuthenticate = async ()=>{
        const authToken = cookies.getAuthToken();
        await setAuthToken(authToken);
        overlayContext.showSuccessToast(changeEmailSuccessMessages.emailChanged, 'email-verified-successfully-toast');
        handleClose();
        return;
    };
    const handleError = (error)=>{
        if (error.hasServerError(ServerError.InvalidOTP)) {
            setError('otp', {
                message: changeEmailErrorMessages.otpCodeIsInvalid
            });
            return;
        }
        if (error.hasServerError(ServerError.EmailAlreadyRegistered)) {
            setError('otp', {
                message: changeEmailErrorMessages.emailAlreadyRegistered
            });
            return;
        }
        if (error.isTooManyAttemptsError()) {
            handleSetView(ViewType.TooManyAttempts);
            return;
        }
        setError('otp', {
            message: changeEmailErrorMessages.somethingWentWrong
        });
    };
    const verifyEmailWithOTPCode = async (param)=>{
        let { otp } = param;
        setIsLoading(true);
        const changeEmailResult = await handleRequest(api.user.verifyEmailWithOTP({
            email: newUserEmail,
            otp
        }));
        if (changeEmailResult.success) {
            await handleChangeEmailAndAuthenticate();
        } else {
            handleError(changeEmailResult.error);
        }
        if (isMounted()) {
            setIsLoading(false);
        }
    };
    const onSendOTPCode = async (param)=>{
        let { showSuccessToast } = param;
        await sendOTPCode({
            requestOTP: ()=>api.user.changeUserEmail({
                    email: newUserEmail
                }),
            onTooManyRequests: ()=>handleSetView(ViewType.TooManyAttempts),
            showSuccessToast
        });
    };
    const resendCode = async ()=>{
        reset();
        await onSendOTPCode({
            showSuccessToast: true
        });
    };
    const otpValue = watch('otp');
    useEffect(()=>{
        clearErrors('otp');
    }, [
        otpValue
    ]);
    return /*#__PURE__*/ _jsx("div", {
        "data-testid": VerifyNewEmailTID.Root(),
        children: /*#__PURE__*/ _jsxs("form", {
            onSubmit: handleSubmit(verifyEmailWithOTPCode),
            children: [
                /*#__PURE__*/ _jsxs("div", {
                    className: commonStyles.content,
                    children: [
                        /*#__PURE__*/ _jsx(Typography, {
                            variant: "paragraph1",
                            testId: VerifyNewEmailTID.InfoText(),
                            children: changeEmailModalTexts.verifyInfo()
                        }),
                        /*#__PURE__*/ _jsx(TextField, {
                            bi: {
                                name: 'verify_new_email'
                            },
                            collapseHelperText: false,
                            width: 360,
                            mobile: {
                                fluid: true
                            },
                            testId: VerifyNewEmailTID.EmailInput(),
                            type: "email",
                            label: changeEmailModalTexts.newEmailLabel(),
                            value: newUserEmail,
                            disabled: true
                        }),
                        /*#__PURE__*/ _jsx(OTPInputSection, {
                            isLoading: isLoading,
                            control: control,
                            resendCode: resendCode
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: commonStyles.footer,
                    children: [
                        /*#__PURE__*/ _jsx(Button, {
                            bi: {
                                name: 'cancel_verify_new_email'
                            },
                            onClick: handleClose,
                            skin: "primaryOutlineOpaque",
                            testId: VerifyNewEmailTID.CancelButton(),
                            children: changeEmailModalTexts.cancelButton()
                        }),
                        /*#__PURE__*/ _jsx(Button, {
                            bi: {
                                name: 'verify_new_email_otp'
                            },
                            type: "submit",
                            testId: VerifyNewEmailTID.SubmitButton(),
                            loading: isLoading,
                            children: changeEmailModalTexts.verifyAndUpdateButton()
                        })
                    ]
                })
            ]
        })
    });
};
export default VerifyNewEmail;
